// ============================================================
// levelData.ts — Level data types, segment prefabs, serialization
// ============================================================

import * as THREE from 'three';

// === RENDER TYPE CONSTANTS (matching C bridge) ===
export const RENDER_PLATFORM = 0;
export const RENDER_WALL = 1;
export const RENDER_RAMP = 2;
export const RENDER_PEG = 3;
export const RENDER_SPLITTER = 4;
export const RENDER_MARBLE = 5;
export const RENDER_FINISH = 6;
export const RENDER_FUNNEL = 7;

// === MARBLE COLORS (matching C) ===
export const MARBLE_COLORS = [
  [0.95, 0.25, 0.20], // Red
  [0.20, 0.50, 0.95], // Blue
  [0.20, 0.80, 0.30], // Green
  [0.95, 0.75, 0.10], // Yellow
  [0.95, 0.40, 0.10], // Orange
  [0.60, 0.25, 0.85], // Purple
  [0.95, 0.30, 0.60], // Pink
  [0.30, 0.85, 0.85], // Cyan
];

// === TYPES ===

export type SegmentType =
  | 'start_platform'
  | 'straight_ramp'
  | 'flat_track'
  | 'peg_section'
  | 'splitter'
  | 'bumper'
  | 'finish_line'
  | 'wall'
  | 'curved_wall'
  | 'bump_section'
  | 'notch_walls'
  | 'slalom'
  | 'funnel'
  | 'steps_staircase'
  | 'wave_track'
  | 'tunnel'
  | 'banked_turn'
  | 'cascade'
  | 's_curve'
  | 'pinball_bumpers'
  | 'trampoline'
  | 'sawtooth'
  | 'grid_ramp'
  | 'choke_point'
  | 'hairpin'
  | 'staggered_pegs'
  | 'jump_gap'
  | 'teeth_walls'
  | 'corner_left'
  | 'corner_right';

export interface Segment {
  id: string;
  type: SegmentType;
  position: [number, number, number];
  rotation: [number, number, number]; // Euler XYZ
  scale?: [number, number, number]; // defaults to [1,1,1]
  properties: Record<string, number>;
}

export interface LevelData {
  name: string;
  author: string;
  version: number;
  segments: Segment[];
}

export interface BodyConfig {
  func: 'box' | 'rotated_box' | 'axis_rotated_box' | 'sphere';
  position: [number, number, number];
  halfExtents?: [number, number, number];
  rotationX?: number;
  axis?: [number, number, number];
  angle?: number;
  radius?: number;
  density: number;
  friction: number;
  restitution: number;
  shapeType: number;
  color: [number, number, number];
  isSensor: boolean;
  velocity?: [number, number, number];
}

export interface SegmentPrefab {
  type: SegmentType;
  label: string;
  color: string;
  defaultProperties: Record<string, number>;
  createVisual: (seg: Segment) => THREE.Group;
  createBodyConfigs: (seg: Segment) => BodyConfig[];
}

// === PREFAB DEFINITIONS ===

function standardWall(
  px: number, py: number, pz: number,
  hx: number, hy: number, hz: number,
  color?: [number, number, number]
): BodyConfig {
  return {
    func: 'box',
    position: [px, py, pz],
    halfExtents: [hx, hy, hz],
    density: 0,
    friction: 0.5,
    restitution: 0,
    shapeType: RENDER_WALL,
    color: color ?? [0.2, 0.45, 0.8],
    isSensor: false,
  };
}

function standardFloor(
  px: number, py: number, pz: number,
  hx: number, hy: number, hz: number,
  shapeType: number = RENDER_PLATFORM,
  color?: [number, number, number]
): BodyConfig {
  return {
    func: 'box',
    position: [px, py, pz],
    halfExtents: [hx, hy, hz],
    density: 0,
    friction: 0.4,
    restitution: 0,
    shapeType,
    color: color ?? [0.95, 0.95, 0.95],
    isSensor: false,
  };
}

// Helper to create wall visual
function makeWallBox(w: number, h: number, d: number, color: string): THREE.Mesh {
  const geo = new THREE.BoxGeometry(w, h, d);
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.6, metalness: 0.05 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function makeFloorBox(w: number, h: number, d: number, color: string): THREE.Mesh {
  const geo = new THREE.BoxGeometry(w, h, d);
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.8, metalness: 0.0 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  return mesh;
}

function makePegSphere(r: number, color: string): THREE.Mesh {
  const geo = new THREE.SphereGeometry(r, 16, 12);
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.3, metalness: 0.3 });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.castShadow = true;
  return mesh;
}

// === WALL VISUAL HELPERS ===

function addStandardSideWalls(
  group: THREE.Group,
  width: number,
  depth: number,
  wallHeight: number = 1.5,
  color: string = '#4488dd'
): void {
  const hw = width / 2;
  const wt = 0.2;
  const left = makeWallBox(wt, wallHeight, depth, color);
  left.position.set(-hw - 0.1, wallHeight / 2, 0);
  group.add(left);
  const right = makeWallBox(wt, wallHeight, depth, color);
  right.position.set(hw + 0.1, wallHeight / 2, 0);
  group.add(right);
}

function addRotatedXSideWalls(
  group: THREE.Group,
  width: number,
  depth: number,
  angle: number,
  wallHeight: number = 1.5,
  color: string = '#4488dd'
): void {
  const hw = width / 2;
  const wt = 0.2;
  const left = makeWallBox(wt, wallHeight, depth, color);
  left.position.set(-hw - 0.1, 0, 0);
  left.rotation.x = angle;
  group.add(left);
  const right = makeWallBox(wt, wallHeight, depth, color);
  right.position.set(hw + 0.1, 0, 0);
  right.rotation.x = angle;
  group.add(right);
}

export function addRotatedZSideWalls(
  group: THREE.Group,
  width: number,
  depth: number,
  angle: number,
  wallHeight: number = 1.5,
  color: string = '#4488dd'
): void {
  const hw = width / 2;
  const wt = 0.2;
  const wY = Math.sin(angle) * (hw + 0.1);
  const left = makeWallBox(wt, wallHeight, depth, color);
  left.position.set(-(hw + 0.1), wallHeight / 2 + wY, 0);
  left.rotation.z = angle;
  group.add(left);
  const right = makeWallBox(wt, wallHeight, depth, color);
  right.position.set(hw + 0.1, wallHeight / 2 - wY, 0);
  right.rotation.z = angle;
  group.add(right);
}

// === SEGMENT TRANSFORM ===
// Apply segment rotation + scale to body positions and orientations
export function transformSegmentBodies(bodies: BodyConfig[], seg: Segment): BodyConfig[] {
  const rx = seg.rotation[0], ry = seg.rotation[1], rz = seg.rotation[2];
  const sx = seg.scale?.[0] ?? 1, sy = seg.scale?.[1] ?? 1, sz = seg.scale?.[2] ?? 1;
  const hasRot = Math.abs(rx) > 0.001 || Math.abs(ry) > 0.001 || Math.abs(rz) > 0.001;
  const hasScale = Math.abs(sx - 1) > 0.001 || Math.abs(sy - 1) > 0.001 || Math.abs(sz - 1) > 0.001;
  if (!hasRot && !hasScale) return bodies;

  const segQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz));
  const segPos = new THREE.Vector3(seg.position[0], seg.position[1], seg.position[2]);

  return bodies.map(body => {
    const localPos = new THREE.Vector3(
      body.position[0] - segPos.x, body.position[1] - segPos.y, body.position[2] - segPos.z
    );
    localPos.x *= sx; localPos.y *= sy; localPos.z *= sz;
    localPos.applyQuaternion(segQuat);

    const nb = { ...body };
    nb.position = [segPos.x + localPos.x, segPos.y + localPos.y, segPos.z + localPos.z];

    if (nb.halfExtents && hasScale) {
      nb.halfExtents = [nb.halfExtents[0] * sx, nb.halfExtents[1] * sy, nb.halfExtents[2] * sz];
    }
    if (nb.radius && hasScale) {
      nb.radius *= (sx + sy + sz) / 3;
    }

    if (hasRot && body.func !== 'sphere') {
      let bodyQuat = new THREE.Quaternion();
      if (body.func === 'rotated_box' && body.rotationX) {
        bodyQuat.setFromEuler(new THREE.Euler(body.rotationX, 0, 0));
      } else if (body.func === 'axis_rotated_box' && body.axis && body.angle) {
        const ax = new THREE.Vector3(body.axis[0], body.axis[1], body.axis[2]).normalize();
        if (ax.length() > 0.001) bodyQuat.setFromAxisAngle(ax, body.angle);
      }
      const composed = new THREE.Quaternion().multiplyQuaternions(segQuat, bodyQuat);

      const compAxis = new THREE.Vector3(0, 1, 0);
      let compAngle = 0;
      if (Math.abs(composed.w) < 0.9999) {
        compAngle = 2 * Math.acos(Math.max(-1, Math.min(1, composed.w)));
        const s = Math.sin(compAngle / 2);
        if (s > 0.001) compAxis.set(composed.x / s, composed.y / s, composed.z / s).normalize();
      }
      nb.func = 'axis_rotated_box';
      nb.axis = [compAxis.x, compAxis.y, compAxis.z];
      nb.angle = compAngle;
      delete nb.rotationX;
    }
    return nb;
  });
}

export const PREFABS: Record<SegmentType, SegmentPrefab> = {
  start_platform: {
    type: 'start_platform',
    label: 'Start Platform',
    color: '#ffffff',
    defaultProperties: { width: 8, depth: 5, wallHeight: 1.5 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const wh = seg.properties.wallHeight;
      const hw = w / 2, hd = d / 2;
      // Floor
      g.add(makeFloorBox(w, 0.5, d, '#e8e8e8'));
      // Left wall
      g.add(makeWallBox(0.2, wh, d, '#4488dd'));
      // Right wall
      g.add(makeWallBox(0.2, wh, d, '#4488dd'));
      // Back wall
      g.add(makeWallBox(w + 0.4, wh, 0.2, '#4488dd'));
      // Marble spawn indicators (small colored spheres)
      const marbleSize = 0.35;
      for (let i = 0; i < 8; i++) {
        const px = -hw + 0.6 + i * (w - 1.2) / 7;
        const sphere = new THREE.Mesh(
          new THREE.SphereGeometry(marbleSize * 0.8, 8, 6),
          new THREE.MeshStandardMaterial({ color: MARBLE_COLORS[i], roughness: 0.15, metalness: 0.5 })
        );
        sphere.position.set(px, marbleSize + 0.25, -hd + 0.6);
        g.add(sphere);
      }
      // Position children
      g.children.forEach(c => {
        if (c.position.x === 0 && c.position.z === 0) {
          // floor stays at origin
        } else if (Math.abs(c.position.x) > hw - 0.5) {
          // side walls
        }
      });
      // Reposition: walls at edges, floor at center
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const wh = seg.properties.wallHeight;
      const hw = w / 2, hd = d / 2;
      const sideX = hw + 0.15;
      return [
        // Floor
        standardFloor(x, y, z, hw, 0.25, hd),
        // Left wall
        standardWall(x - sideX, y + wh / 2, z, 0.1, wh / 2, hd),
        // Right wall
        standardWall(x + sideX, y + wh / 2, z, 0.1, wh / 2, hd),
        // Back wall
        standardWall(x, y + wh / 2, z - hd - 0.1, hw + 0.15, wh / 2, 0.1),
      ];
    },
  },

  straight_ramp: {
    type: 'straight_ramp',
    label: 'Straight Ramp',
    color: '#c8b89a',
    defaultProperties: { width: 8, depth: 10, dropHeight: 4.75 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const hw = w / 2, hd = d / 2;
      const angle = Math.atan2(seg.properties.dropHeight, d * 2);
      // Ramp surface
      const ramp = makeFloorBox(w, 1, d, '#c8b89a');
      ramp.rotation.x = angle;
      g.add(ramp);
      addRotatedXSideWalls(g, w, d, angle);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const dh = seg.properties.dropHeight;
      const hw = w / 2, hd = d / 2;
      const angle = Math.atan2(dh, d * 2);
      const sideX = hw + 0.15;
      return [
        {
          func: 'rotated_box',
          position: [x, y, z],
          halfExtents: [hw, 0.5, hd],
          rotationX: angle,
          density: 0,
          friction: 0.35,
          restitution: 0.02,
          shapeType: RENDER_RAMP,
          color: [0.78, 0.70, 0.58],
          isSensor: false,
        },
        // Side walls follow ramp angle
        {
          func: 'rotated_box',
          position: [x - sideX, y, z],
          halfExtents: [0.1, 0.75, hd],
          rotationX: angle,
          density: 0,
          friction: 0.5,
          restitution: 0,
          shapeType: RENDER_WALL,
          color: [0.2, 0.45, 0.8],
          isSensor: false,
        },
        {
          func: 'rotated_box',
          position: [x + sideX, y, z],
          halfExtents: [0.1, 0.75, hd],
          rotationX: angle,
          density: 0,
          friction: 0.5,
          restitution: 0,
          shapeType: RENDER_WALL,
          color: [0.2, 0.45, 0.8],
          isSensor: false,
        },
      ];
    },
  },

  flat_track: {
    type: 'flat_track',
    label: 'Flat Track',
    color: '#e0e0e0',
    defaultProperties: { width: 8, depth: 6 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const w = seg.properties.width;
      const d = seg.properties.depth;
      g.add(makeFloorBox(w, 0.5, d, '#e0e0e0'));
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const hw = w / 2, hd = d / 2;
      const sideX = hw + 0.15;
      return [
        standardFloor(x, y, z, hw, 0.25, hd),
        standardWall(x - sideX, y + 0.75, z, 0.1, 0.75, hd),
        standardWall(x + sideX, y + 0.75, z, 0.1, 0.75, hd),
      ];
    },
  },

  peg_section: {
    type: 'peg_section',
    label: 'Peg Section',
    color: '#dd4444',
    defaultProperties: { width: 8, depth: 4, rows: 2, cols: 3 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const rows = seg.properties.rows;
      const cols = seg.properties.cols;
      g.add(makeFloorBox(w, 0.5, d, '#d8d8d8'));
      // Peg grid
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const px = -w / 2 + 1 + c * (w - 2) / Math.max(cols - 1, 1);
          const pz = -d / 2 + 1 + r * (d - 2) / Math.max(rows - 1, 1);
          const peg = makePegSphere(0.25, '#cc3333');
          peg.position.set(px, 0.25, pz);
          g.add(peg);
        }
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const rows = seg.properties.rows;
      const cols = seg.properties.cols;
      const hw = w / 2, hd = d / 2;
      const sideX = hw + 0.15;
      const bodies: BodyConfig[] = [
        standardFloor(x, y, z, hw, 0.25, hd),
        standardWall(x - sideX, y + 0.75, z, 0.1, 0.75, hd),
        standardWall(x + sideX, y + 0.75, z, 0.1, 0.75, hd),
      ];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const px = x - hw + 1 + c * (w - 2) / Math.max(cols - 1, 1);
          const pz = z - hd + 1 + r * (d - 2) / Math.max(rows - 1, 1);
          bodies.push({
            func: 'sphere',
            position: [px, y, pz],
            radius: 0.25,
            density: 0,
            friction: 0.3,
            restitution: 0.3,
            shapeType: RENDER_PEG,
            color: [0.8, 0.2, 0.2],
            isSensor: false,
          });
        }
      }
      return bodies;
    },
  },

  splitter: {
    type: 'splitter',
    label: 'Splitter',
    color: '#ff8800',
    defaultProperties: { width: 2.5, depth: 4, wallHeight: 1.3 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const hw = seg.properties.width / 2;
      const hd = seg.properties.depth / 2;
      const wh = seg.properties.wallHeight;
      // Center wall
      g.add(makeWallBox(0.16, wh, hd * 2, '#ff8800'));
      // Left angled wall
      const left = makeWallBox(0.16, wh, hd, '#ff8800');
      left.rotation.y = -0.45;
      left.position.set(-hw * 0.5, 0, hd * 0.5);
      g.add(left);
      // Right angled wall
      const right = makeWallBox(0.16, wh, hd, '#ff8800');
      right.rotation.y = 0.45;
      right.position.set(hw * 0.5, 0, hd * 0.5);
      g.add(right);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const hw = seg.properties.width / 2;
      const hd = seg.properties.depth / 2;
      const wh = seg.properties.wallHeight;
      const halfH = wh / 2;
      return [
        // Center wall
        {
          func: 'box',
          position: [x, y + halfH, z],
          halfExtents: [0.08, halfH, hd],
          density: 0, friction: 0.5, restitution: 0,
          shapeType: RENDER_SPLITTER, color: [0.95, 0.55, 0.1], isSensor: false,
        },
        // Left angled wall
        {
          func: 'axis_rotated_box',
          position: [x - hw * 0.4, y + halfH, z + hd * 0.4],
          halfExtents: [0.08, halfH, hd * 0.6],
          axis: [0, 1, 0], angle: -0.45,
          density: 0, friction: 0.5, restitution: 0,
          shapeType: RENDER_SPLITTER, color: [0.95, 0.55, 0.1], isSensor: false,
        },
        // Right angled wall
        {
          func: 'axis_rotated_box',
          position: [x + hw * 0.4, y + halfH, z + hd * 0.4],
          halfExtents: [0.08, halfH, hd * 0.6],
          axis: [0, 1, 0], angle: 0.45,
          density: 0, friction: 0.5, restitution: 0,
          shapeType: RENDER_SPLITTER, color: [0.95, 0.55, 0.1], isSensor: false,
        },
      ];
    },
  },

  bumper: {
    type: 'bumper',
    label: 'Bumper',
    color: '#dd3333',
    defaultProperties: { width: 0.5, height: 1.5, depth: 0.5 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      g.add(makeWallBox(seg.properties.width, seg.properties.height, seg.properties.depth, '#dd3333'));
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const hw = seg.properties.width / 2;
      const hh = seg.properties.height / 2;
      const hd = seg.properties.depth / 2;
      return [{
        func: 'box', position: [x, y + hh, z],
        halfExtents: [hw, hh, hd],
        density: 0, friction: 0.5, restitution: 0.4,
        shapeType: RENDER_WALL, color: [0.85, 0.2, 0.2], isSensor: false,
      }];
    },
  },

  finish_line: {
    type: 'finish_line',
    label: 'Finish Line',
    color: '#33cc55',
    defaultProperties: { width: 8, depth: 4, sensorCount: 3 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const w = seg.properties.width;
      const d = seg.properties.depth;
      g.add(makeFloorBox(w, 0.5, d, '#d8d8d8'));
      // Sensor boxes
      const sensorMat = new THREE.MeshStandardMaterial({
        color: '#33ee55', transparent: true, opacity: 0.4, roughness: 0.3
      });
      for (let i = 0; i < seg.properties.sensorCount; i++) {
        const px = -w / 2 + 1.5 + i * (w - 3) / Math.max(seg.properties.sensorCount - 1, 1);
        const sensor = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.8, 0.2), sensorMat);
        sensor.position.set(px, 0.4, 0);
        g.add(sensor);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const sc = seg.properties.sensorCount;
      const hw = w / 2, hd = d / 2;
      const sideX = hw + 0.15;
      const bodies: BodyConfig[] = [
        standardFloor(x, y, z, hw, 0.25, hd),
        standardWall(x - sideX, y + 0.75, z, 0.1, 0.75, hd),
        standardWall(x + sideX, y + 0.75, z, 0.1, 0.75, hd),
      ];
      for (let i = 0; i < sc; i++) {
        const px = x - hw + 1.5 + i * (w - 3) / Math.max(sc - 1, 1);
        bodies.push({
          func: 'box', position: [px, y + 0.4, z + hd * 0.85],
          halfExtents: [0.6, 0.8, 0.1],
          density: 0, friction: 0.5, restitution: 0,
          shapeType: RENDER_FINISH, color: [0.1, 0.9, 0.3], isSensor: true,
        });
      }
      return bodies;
    },
  },

  wall: {
    type: 'wall',
    label: 'Wall Segment',
    color: '#4488dd',
    defaultProperties: { width: 0.2, height: 1.5, depth: 4 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      g.add(makeWallBox(seg.properties.width, seg.properties.height, seg.properties.depth, '#4488dd'));
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      return [{
        func: 'box', position: [x, y + seg.properties.height / 2, z],
        halfExtents: [seg.properties.width / 2, seg.properties.height / 2, seg.properties.depth / 2],
        density: 0, friction: 0.5, restitution: 0,
        shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false,
      }];
    },
  },

  // === NEW SEGMENTS ===

  curved_wall: {
    type: 'curved_wall',
    label: 'Curved Wall',
    color: '#44aadd',
    defaultProperties: { radius: 4, height: 1.5, arcDegrees: 180, segments: 8 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const r = seg.properties.radius;
      const h = seg.properties.height;
      const arc = THREE.MathUtils.degToRad(seg.properties.arcDegrees);
      const count = seg.properties.segments;
      for (let i = 0; i < count; i++) {
        const angle = -arc/2 + (i + 0.5) * arc / count;
        const wall = makeWallBox(0.3, h, r * Math.tan(arc / count / 2) * 1.5, '#44aadd');
        wall.position.set(r * Math.cos(angle), 0, r * Math.sin(angle));
        wall.rotation.y = -angle;
        g.add(wall);
      }
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const r = seg.properties.radius;
      const h = seg.properties.height;
      const arc = seg.properties.arcDegrees * Math.PI / 180;
      const count = seg.properties.segments;
      const halfH = h / 2;
      const halfW = 0.15;
      const halfD = r * Math.tan(arc / count / 2) * 1.5;
      const bodies: BodyConfig[] = [];
      for (let i = 0; i < count; i++) {
        const angle = -arc/2 + (i + 0.5) * arc / count;
        const px = x + r * Math.cos(angle);
        const pz = z + r * Math.sin(angle);
        bodies.push({
          func: 'axis_rotated_box',
          position: [px, y + halfH, pz],
          halfExtents: [halfW, halfH, halfD],
          axis: [0, 1, 0], angle: -angle,
          density: 0, friction: 0.5, restitution: 0.2,
          shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false,
        });
      }
      return bodies;
    },
  },

  bump_section: {
    type: 'bump_section',
    label: 'Bump Section',
    color: '#cc8844',
    defaultProperties: { width: 8, depth: 6, bumpHeight: 0.4, bumpCount: 3 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const bh = seg.properties.bumpHeight;
      const bc = seg.properties.bumpCount;
      g.add(makeFloorBox(w, 0.5, d, '#d4c8b8'));
      for (let i = 0; i < bc; i++) {
        const bump = makeWallBox(w * 0.5, bh, d / bc * 0.5, '#bb7744');
        bump.position.set(0, bh / 2, -d/2 + (i + 0.5) * d / bc);
        g.add(bump);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const bh = seg.properties.bumpHeight;
      const bc = seg.properties.bumpCount;
      const hw = w / 2, hd = d / 2;
      const sideX = hw + 0.15;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd],
          density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM,
          color: [0.85, 0.78, 0.72], isSensor: false },
        { func: 'box', position: [x - sideX, y + 0.75, z], halfExtents: [0.1, 0.75, hd],
          density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL,
          color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sideX, y + 0.75, z], halfExtents: [0.1, 0.75, hd],
          density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL,
          color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      for (let i = 0; i < bc; i++) {
        const pz = z - hd + (i + 0.5) * d / bc;
        bodies.push({
          func: 'box', position: [x, y + bh / 2, pz],
          halfExtents: [hw * 0.25, bh / 2, d / bc * 0.25],
          density: 0, friction: 0.5, restitution: 0.15,
          shapeType: RENDER_WALL, color: [0.73, 0.47, 0.27], isSensor: false,
        });
      }
      return bodies;
    },
  },

  notch_walls: {
    type: 'notch_walls',
    label: 'Notch Walls',
    color: '#dd6655',
    defaultProperties: { width: 8, depth: 6, notchDepth: 1.5, notchCount: 3 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const nd = seg.properties.notchDepth;
      const nc = seg.properties.notchCount;
      g.add(makeFloorBox(w, 0.5, d, '#d8d0c8'));
      for (let i = 0; i < nc; i++) {
        const pz = -d/2 + (i + 0.5) * d / nc;
        // Left notch wall (angled inward)
        const left = makeWallBox(0.2, 1.0, 1.0, '#dd6655');
        left.position.set(-w/2 + nd/2, 0.5, pz);
        left.rotation.z = 0.3;
        g.add(left);
        // Right notch wall
        const right = makeWallBox(0.2, 1.0, 1.0, '#dd6655');
        right.position.set(w/2 - nd/2, 0.5, pz);
        right.rotation.z = -0.3;
        g.add(right);
      }
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const nd = seg.properties.notchDepth;
      const nc = seg.properties.notchCount;
      const hw = w / 2, hd = d / 2;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd],
          density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM,
          color: [0.88, 0.82, 0.78], isSensor: false },
      ];
      for (let i = 0; i < nc; i++) {
        const pz = z - hd + (i + 0.5) * d / nc;
        // Left notch
        bodies.push({
          func: 'axis_rotated_box', position: [x - hw + nd/2, y + 0.5, pz],
          halfExtents: [0.1, 0.5, 0.5], axis: [0, 0, 1], angle: 0.3,
          density: 0, friction: 0.5, restitution: 0.4,
          shapeType: RENDER_WALL, color: [0.87, 0.4, 0.33], isSensor: false,
        });
        // Right notch
        bodies.push({
          func: 'axis_rotated_box', position: [x + hw - nd/2, y + 0.5, pz],
          halfExtents: [0.1, 0.5, 0.5], axis: [0, 0, 1], angle: -0.3,
          density: 0, friction: 0.5, restitution: 0.4,
          shapeType: RENDER_WALL, color: [0.87, 0.4, 0.33], isSensor: false,
        });
      }
      return bodies;
    },
  },

  slalom: {
    type: 'slalom',
    label: 'Slalom',
    color: '#44bb88',
    defaultProperties: { width: 8, depth: 6, gateCount: 5, gateWidth: 2 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const gc = seg.properties.gateCount;
      const gw = seg.properties.gateWidth;
      const alt = (w - gw) / 2;
      g.add(makeFloorBox(w, 0.5, d, '#c8d8d0'));
      for (let i = 0; i < gc; i++) {
        const side = (i % 2 === 0) ? -1 : 1;
        const wall = makeWallBox(0.2, 1.0, 0.5, '#44bb88');
        wall.position.set(side * alt, 0.5, -d/2 + (i + 0.5) * d / gc);
        g.add(wall);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const gc = seg.properties.gateCount;
      const gw = seg.properties.gateWidth;
      const hw = w / 2, hd = d / 2;
      const alt = (w - gw) / 2;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd],
          density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM,
          color: [0.78, 0.85, 0.82], isSensor: false },
        { func: 'box', position: [x - hw - 0.15, y + 0.75, z], halfExtents: [0.1, 0.75, hd],
          density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL,
          color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + hw + 0.15, y + 0.75, z], halfExtents: [0.1, 0.75, hd],
          density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL,
          color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      for (let i = 0; i < gc; i++) {
        const side = (i % 2 === 0) ? -1 : 1;
        const pz = z - hd + (i + 0.5) * d / gc;
        bodies.push({
          func: 'box', position: [x + side * alt, y + 0.5, pz],
          halfExtents: [0.1, 0.5, 0.25],
          density: 0, friction: 0.5, restitution: 0.3,
          shapeType: RENDER_WALL, color: [0.27, 0.73, 0.53], isSensor: false,
        });
      }
      return bodies;
    },
  },

  funnel: {
    type: 'funnel',
    label: 'Funnel',
    color: '#aa66dd',
    defaultProperties: { width: 8, depth: 5, exitWidth: 1.5 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const ew = seg.properties.exitWidth;
      g.add(makeFloorBox(w, 0.5, d, '#d0c8d8'));
      // Left angled wall
      const left = makeWallBox(0.2, 1.0, d, '#aa66dd');
      left.position.set(-(w + ew) / 4, 0.5, 0);
      left.rotation.y = Math.atan2((w - ew) / 2, d);
      g.add(left);
      // Right angled wall
      const right = makeWallBox(0.2, 1.0, d, '#aa66dd');
      right.position.set((w + ew) / 4, 0.5, 0);
      right.rotation.y = -Math.atan2((w - ew) / 2, d);
      g.add(right);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const w = seg.properties.width;
      const d = seg.properties.depth;
      const ew = seg.properties.exitWidth;
      const hw = w / 2, hd = d / 2;
      const angle = Math.atan2((w - ew) / 2, d);
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd],
          density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM,
          color: [0.82, 0.78, 0.85], isSensor: false },
      ];
      // Left converging wall
      bodies.push({
        func: 'axis_rotated_box',
        position: [x - (w + ew) / 4, y + 0.5, z],
        halfExtents: [0.1, 0.5, hd],
        axis: [0, 1, 0], angle: angle,
        density: 0, friction: 0.5, restitution: 0.3,
        shapeType: RENDER_WALL, color: [0.67, 0.4, 0.87], isSensor: false,
      });
      // Right converging wall
      bodies.push({
        func: 'axis_rotated_box',
        position: [x + (w + ew) / 4, y + 0.5, z],
        halfExtents: [0.1, 0.5, hd],
        axis: [0, 1, 0], angle: -angle,
        density: 0, friction: 0.5, restitution: 0.3,
        shapeType: RENDER_WALL, color: [0.67, 0.4, 0.87], isSensor: false,
      });
      return bodies;
    },
  },

  // === 15 NEW SEGMENTS ===

  steps_staircase: {
    type: 'steps_staircase', label: 'Steps Staircase', color: '#b8a080',
    defaultProperties: { width: 8, depth: 5, stepCount: 5, stepHeight: 0.3 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      const sc = seg.properties.stepCount; const sh = seg.properties.stepHeight;
      g.add(makeFloorBox(w, 0.5, d, '#b8a080'));
      for (let i = 0; i < sc; i++) {
        const step = makeWallBox(w * 0.8, sh, d / sc * 0.7, '#c8b898');
        step.position.set(0, sh / 2 + i * sh, -d/2 + (i + 0.5) * d / sc);
        g.add(step);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const sc = seg.properties.stepCount; const sh = seg.properties.stepHeight;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.72, 0.63, 0.5], isSensor: false },
        { func: 'box', position: [x - sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      for (let i = 0; i < sc; i++) {
        bodies.push({ func: 'box', position: [x, y + sh / 2 + i * sh, z - hd + (i + 0.5) * d / sc], halfExtents: [hw * 0.4, sh / 2, d / sc * 0.35], density: 0, friction: 0.5, restitution: 0.1, shapeType: RENDER_WALL, color: [0.78, 0.72, 0.6], isSensor: false });
      }
      return bodies;
    },
  },

  wave_track: {
    type: 'wave_track', label: 'Wave Track', color: '#44bbdd',
    defaultProperties: { width: 8, depth: 8, waveCount: 3, waveHeight: 0.6 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      const wc = seg.properties.waveCount; const wh = seg.properties.waveHeight;
      g.add(makeFloorBox(w, 0.5, d, '#b0d8e0'));
      for (let i = 0; i < wc; i++) {
        const up = makeWallBox(w * 0.3, wh, d / wc * 0.3, '#44bbdd');
        up.position.set(0, -wh/2, -d/2 + (i + 0.3) * d / wc); up.rotation.x = 0.3 + i * 0.05;
        g.add(up);
        const dn = makeWallBox(w * 0.3, wh, d / wc * 0.3, '#44bbdd');
        dn.position.set(0, -wh/2, -d/2 + (i + 0.7) * d / wc); dn.rotation.x = -0.3 - i * 0.05;
        g.add(dn);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const wc = seg.properties.waveCount; const wh = seg.properties.waveHeight;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.69, 0.85, 0.88], isSensor: false },
        { func: 'box', position: [x - sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      const angle = 0.35;
      for (let i = 0; i < wc; i++) {
        const pz1 = z - hd + (i + 0.3) * d / wc;
        bodies.push({ func: 'rotated_box', position: [x, y + wh/2, pz1], halfExtents: [hw * 0.15, wh/2, d / wc * 0.15], rotationX: angle + i * 0.03, density: 0, friction: 0.5, restitution: 0.15, shapeType: RENDER_RAMP, color: [0.27, 0.73, 0.87], isSensor: false });
        const pz2 = z - hd + (i + 0.7) * d / wc;
        bodies.push({ func: 'rotated_box', position: [x, y + wh/2, pz2], halfExtents: [hw * 0.15, wh/2, d / wc * 0.15], rotationX: -angle - i * 0.03, density: 0, friction: 0.5, restitution: 0.15, shapeType: RENDER_RAMP, color: [0.27, 0.73, 0.87], isSensor: false });
      }
      return bodies;
    },
  },

  tunnel: {
    type: 'tunnel', label: 'Tunnel', color: '#666688',
    defaultProperties: { width: 8, depth: 4, height: 1.2 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth; const h = seg.properties.height;
      g.add(makeFloorBox(w, 0.5, d, '#8888aa'));
      g.add(makeWallBox(0.2, h, d, '#555577'));
      g.add(makeWallBox(0.2, h, d, '#555577'));
      const top = makeFloorBox(w - 0.4, 0.1, d, '#777799');
      top.material = new THREE.MeshStandardMaterial({ color: '#777799', transparent: true, opacity: 0.4 });
      top.position.y = h;
      g.add(top);
      // Position walls at edges
      (g.children[1] as THREE.Mesh).position.x = -w / 2 + 0.1;
      (g.children[2] as THREE.Mesh).position.x = w / 2 - 0.1;
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth; const h = seg.properties.height;
      const hw = w / 2, hd = d / 2;
      return [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.53, 0.53, 0.67], isSensor: false },
        { func: 'box', position: [x - hw + 0.1, y + h/2, z], halfExtents: [0.1, h/2, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.33, 0.33, 0.47], isSensor: false },
        { func: 'box', position: [x + hw - 0.1, y + h/2, z], halfExtents: [0.1, h/2, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.33, 0.33, 0.47], isSensor: false },
        { func: 'box', position: [x, y + h, z], halfExtents: [hw - 0.2, 0.05, hd], density: 0, friction: 0.3, restitution: 0.1, shapeType: RENDER_WALL, color: [0.47, 0.47, 0.6], isSensor: false },
      ];
    },
  },

  banked_turn: {
    type: 'banked_turn', label: 'Banked Turn', color: '#88aa44',
    defaultProperties: { width: 8, depth: 6, bankAngle: 0.3 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth; const ba = seg.properties.bankAngle;
      const floor = makeFloorBox(w, 0.5, d, '#88aa44');
      floor.rotation.z = ba; g.add(floor);
      addRotatedZSideWalls(g, w, d, ba);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth; const ba = seg.properties.bankAngle;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15; const wAll = Math.sin(ba) * sx;
      return [
        { func: 'axis_rotated_box', position: [x, y, z], halfExtents: [hw, 0.25, hd], axis: [0, 0, 1], angle: ba, density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.53, 0.67, 0.27], isSensor: false },
        { func: 'axis_rotated_box', position: [x - sx, y + wAll, z], halfExtents: [0.1, 0.75, hd], axis: [0, 0, 1], angle: ba, density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'axis_rotated_box', position: [x + sx, y - wAll, z], halfExtents: [0.1, 0.75, hd], axis: [0, 0, 1], angle: ba, density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
    },
  },

  cascade: {
    type: 'cascade', label: 'Cascade Drop', color: '#66aadd',
    defaultProperties: { width: 8, depth: 6, dropCount: 4, dropHeight: 0.4 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      const dc = seg.properties.dropCount; const dh = seg.properties.dropHeight;
      for (let i = 0; i < dc; i++) {
        const yOff = -i * dh;
        const segD = d / dc * 0.9;
        const fl = makeFloorBox(w, 0.3, segD, i % 2 === 0 ? '#66aadd' : '#88bbdd');
        fl.position.set(0, yOff - dh/2, -d/2 + segD/2 + i * d / dc);
        g.add(fl);
        if (i > 0) {
          const wall = makeWallBox(w * 0.8, dh * 0.8, 0.15, '#5588bb');
          wall.position.set(0, yOff, -d/2 + i * d / dc - 0.1);
          g.add(wall);
        }
        // Side walls for this platform level
        const pSegD = segD;
        const leftW = makeWallBox(0.2, 1.0, pSegD, '#4488dd');
        leftW.position.set(-w/2 - 0.1, 0.5 + yOff - dh/2, -d/2 + pSegD/2 + i * d / dc);
        g.add(leftW);
        const rightW = makeWallBox(0.2, 1.0, pSegD, '#4488dd');
        rightW.position.set(w/2 + 0.1, 0.5 + yOff - dh/2, -d/2 + pSegD/2 + i * d / dc);
        g.add(rightW);
      }
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const dc = seg.properties.dropCount; const dh = seg.properties.dropHeight;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      const bodies: BodyConfig[] = [];
      for (let i = 0; i < dc; i++) {
        const yOff = -i * dh;
        const pd = d / dc;
        const pz = z - hd + pd/2 + i * pd;
        bodies.push({ func: 'box', position: [x, y + yOff - dh/2, pz], halfExtents: [hw, 0.15, pd * 0.4], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.4, 0.68, 0.87], isSensor: false });
        bodies.push({ func: 'box', position: [x - sx, y + 0.5 + yOff - dh/2, pz], halfExtents: [0.1, 0.5, pd * 0.4], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false });
        bodies.push({ func: 'box', position: [x + sx, y + 0.5 + yOff - dh/2, pz], halfExtents: [0.1, 0.5, pd * 0.4], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false });
        if (i > 0) {
          bodies.push({ func: 'box', position: [x, y + yOff + dh * 0.4, z - hd + i * pd - 0.1], halfExtents: [hw * 0.4, dh * 0.4, 0.1], density: 0, friction: 0.5, restitution: 0.15, shapeType: RENDER_WALL, color: [0.33, 0.53, 0.73], isSensor: false });
        }
      }
      return bodies;
    },
  },

  s_curve: {
    type: 's_curve', label: 'S-Curve Walls', color: '#dd8844',
    defaultProperties: { width: 8, depth: 6, curveCount: 3, amplitude: 1.5 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      const cc = seg.properties.curveCount; const amp = seg.properties.amplitude;
      g.add(makeFloorBox(w, 0.5, d, '#d8c8b0'));
      for (let i = 0; i < cc; i++) {
        const pz = -d/2 + (i + 0.5) * d / cc;
        const side = (i % 2 === 0) ? 1 : -1;
        const wall = makeWallBox(0.2, 1.0, d / cc * 0.6, '#dd8844');
        wall.position.set(side * amp, 0.5, pz);
        g.add(wall);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const cc = seg.properties.curveCount; const amp = seg.properties.amplitude;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.85, 0.78, 0.69], isSensor: false },
        { func: 'box', position: [x - sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      for (let i = 0; i < cc; i++) {
        const side = (i % 2 === 0) ? 1 : -1;
        bodies.push({ func: 'box', position: [x + side * amp, y + 0.5, z - hd + (i + 0.5) * d / cc], halfExtents: [0.1, 0.5, d / cc * 0.3], density: 0, friction: 0.5, restitution: 0.3, shapeType: RENDER_WALL, color: [0.87, 0.53, 0.27], isSensor: false });
      }
      return bodies;
    },
  },

  pinball_bumpers: {
    type: 'pinball_bumpers', label: 'Pinball Bumpers', color: '#ff4444',
    defaultProperties: { width: 8, depth: 5, bumperCount: 6, bumperRadius: 0.3 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      const bc = seg.properties.bumperCount; const br = seg.properties.bumperRadius;
      g.add(makeFloorBox(w, 0.5, d, '#d8c0c0'));
      const seeds = [0.2, 0.7, 0.5, 0.1, 0.9, 0.3, 0.6, 0.8, 0.4, 0.15];
      for (let i = 0; i < bc; i++) {
        const px = -w/2 + 1 + seeds[i % seeds.length] * (w - 2);
        const pz = -d/2 + 0.8 + seeds[(i+3) % seeds.length] * (d - 1.6);
        const peg = makePegSphere(br, '#ff4444');
        peg.position.set(px, br, pz);
        g.add(peg);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const bc = seg.properties.bumperCount; const br = seg.properties.bumperRadius;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      const seeds = [0.2, 0.7, 0.5, 0.1, 0.9, 0.3, 0.6, 0.8, 0.4, 0.15];
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.85, 0.75, 0.75], isSensor: false },
        { func: 'box', position: [x - sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      for (let i = 0; i < bc; i++) {
        bodies.push({ func: 'sphere', position: [x - hw + 1 + seeds[i % seeds.length] * (w - 2), y, z - hd + 0.8 + seeds[(i+3) % seeds.length] * (d - 1.6)], radius: br, density: 0, friction: 0.2, restitution: 0.8, shapeType: RENDER_PEG, color: [1.0, 0.27, 0.27], isSensor: false, velocity: [0,0,0] });
      }
      return bodies;
    },
  },

  trampoline: {
    type: 'trampoline', label: 'Trampoline', color: '#44ff88',
    defaultProperties: { width: 8, depth: 3, bounceHeight: 1.0 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      g.add(makeFloorBox(w, 0.5, d, '#44ff88'));
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      return [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.3, restitution: 0.9, shapeType: RENDER_PLATFORM, color: [0.27, 1.0, 0.53], isSensor: false },
        { func: 'box', position: [x - sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
    },
  },

  sawtooth: {
    type: 'sawtooth', label: 'Sawtooth', color: '#cc6644',
    defaultProperties: { width: 8, depth: 5, toothCount: 6, toothHeight: 0.5 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      const tc = seg.properties.toothCount; const th = seg.properties.toothHeight;
      g.add(makeFloorBox(w, 0.5, d, '#c8b0a8'));
      for (let i = 0; i < tc; i++) {
        const t1 = makeWallBox(0.15, th, d / tc * 0.2, '#cc6644');
        t1.position.set(-w/4, th/2, -d/2 + (i + 0.3) * d / tc);
        t1.rotation.z = 0.4; g.add(t1);
        const t2 = makeWallBox(0.15, th, d / tc * 0.2, '#cc6644');
        t2.position.set(w/4, th/2, -d/2 + (i + 0.7) * d / tc);
        t2.rotation.z = -0.4; g.add(t2);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const tc = seg.properties.toothCount; const th = seg.properties.toothHeight;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.78, 0.69, 0.66], isSensor: false },
        { func: 'box', position: [x - sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      for (let i = 0; i < tc; i++) {
        bodies.push({ func: 'axis_rotated_box', position: [x - hw/2, y + th/2, z - hd + (i + 0.3) * d / tc], halfExtents: [0.1, th/2, d / tc * 0.1], axis: [0, 0, 1], angle: 0.4, density: 0, friction: 0.5, restitution: 0.2, shapeType: RENDER_WALL, color: [0.8, 0.4, 0.27], isSensor: false });
        bodies.push({ func: 'axis_rotated_box', position: [x + hw/2, y + th/2, z - hd + (i + 0.7) * d / tc], halfExtents: [0.1, th/2, d / tc * 0.1], axis: [0, 0, 1], angle: -0.4, density: 0, friction: 0.5, restitution: 0.2, shapeType: RENDER_WALL, color: [0.8, 0.4, 0.27], isSensor: false });
      }
      return bodies;
    },
  },

  grid_ramp: {
    type: 'grid_ramp', label: 'Grid Ramp', color: '#aabb88',
    defaultProperties: { width: 8, depth: 6, railCount: 5, railWidth: 0.3 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      const rc = seg.properties.railCount; const rw = seg.properties.railWidth;
      const angle = 0.4;
      for (let i = 0; i < rc; i++) {
        const rail = makeWallBox(rw, 0.35, d, '#aabb88');
        rail.position.set(-w/2 + (i + 0.5) * w / rc, 0.175, 0);
        rail.rotation.x = angle;
        g.add(rail);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const rc = seg.properties.railCount; const rw = seg.properties.railWidth;
      const hd = d / 2; const angle = 0.4;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x - w/2 - 0.15, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + w/2 + 0.15, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      for (let i = 0; i < rc; i++) {
        bodies.push({ func: 'rotated_box', position: [x - w/2 + (i + 0.5) * w / rc, y + 0.175, z], halfExtents: [rw/2, 0.175, hd], rotationX: angle, density: 0, friction: 0.35, restitution: 0.02, shapeType: RENDER_RAMP, color: [0.67, 0.73, 0.53], isSensor: false });
      }
      return bodies;
    },
  },

  choke_point: {
    type: 'choke_point', label: 'Choke Point', color: '#dd88aa',
    defaultProperties: { width: 8, depth: 5, minWidth: 1.5 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth; const mw = seg.properties.minWidth;
      g.add(makeFloorBox(w, 0.5, d, '#d8c0c8'));
      const wallLen = d * 0.35;
      const left1 = makeWallBox(0.2, 1.0, wallLen, '#dd88aa');
      left1.position.set(-(w + mw) / 4, 0.5, -d * 0.2);
      left1.rotation.y = 0.5; g.add(left1);
      const left2 = makeWallBox(0.2, 1.0, wallLen, '#dd88aa');
      left2.position.set(-(w + mw) / 4, 0.5, d * 0.2);
      left2.rotation.y = -0.5; g.add(left2);
      const right1 = makeWallBox(0.2, 1.0, wallLen, '#dd88aa');
      right1.position.set((w + mw) / 4, 0.5, -d * 0.2);
      right1.rotation.y = -0.5; g.add(right1);
      const right2 = makeWallBox(0.2, 1.0, wallLen, '#dd88aa');
      right2.position.set((w + mw) / 4, 0.5, d * 0.2);
      right2.rotation.y = 0.5; g.add(right2);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth; const mw = seg.properties.minWidth;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      const wallLen = d * 0.35;
      const a = 0.5;
      return [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.87, 0.75, 0.78], isSensor: false },
        { func: 'box', position: [x - sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'axis_rotated_box', position: [x - (w + mw) / 4, y + 0.5, z - hd * 0.2], halfExtents: [0.1, 0.5, wallLen/2], axis: [0, 1, 0], angle: a, density: 0, friction: 0.5, restitution: 0.3, shapeType: RENDER_WALL, color: [0.87, 0.53, 0.67], isSensor: false },
        { func: 'axis_rotated_box', position: [x - (w + mw) / 4, y + 0.5, z + hd * 0.2], halfExtents: [0.1, 0.5, wallLen/2], axis: [0, 1, 0], angle: -a, density: 0, friction: 0.5, restitution: 0.3, shapeType: RENDER_WALL, color: [0.87, 0.53, 0.67], isSensor: false },
        { func: 'axis_rotated_box', position: [x + (w + mw) / 4, y + 0.5, z - hd * 0.2], halfExtents: [0.1, 0.5, wallLen/2], axis: [0, 1, 0], angle: -a, density: 0, friction: 0.5, restitution: 0.3, shapeType: RENDER_WALL, color: [0.87, 0.53, 0.67], isSensor: false },
        { func: 'axis_rotated_box', position: [x + (w + mw) / 4, y + 0.5, z + hd * 0.2], halfExtents: [0.1, 0.5, wallLen/2], axis: [0, 1, 0], angle: a, density: 0, friction: 0.5, restitution: 0.3, shapeType: RENDER_WALL, color: [0.87, 0.53, 0.67], isSensor: false },
      ];
    },
  },

  hairpin: {
    type: 'hairpin', label: 'Hairpin Turn', color: '#aa6644',
    defaultProperties: { width: 8, depth: 6, turnRadius: 3 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth; const tr = seg.properties.turnRadius;
      g.add(makeFloorBox(w + 2, 0.5, d, '#b8a090'));
      for (let i = 0; i < 8; i++) {
        const a = -Math.PI/2 + (i / 8) * Math.PI;
        const wall = makeWallBox(0.25, 1.0, 0.6, '#aa6644');
        wall.position.set(tr * Math.cos(a), 0.5, d/4 + tr * Math.sin(a));
        wall.rotation.y = -a;
        g.add(wall);
      }
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth; const tr = seg.properties.turnRadius;
      const hd = d / 2;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z + hd/2], halfExtents: [(w + 2)/2, 0.25, hd/2], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.72, 0.63, 0.56], isSensor: false },
        { func: 'box', position: [x, y, z - hd/4], halfExtents: [w/2, 0.25, hd/2], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.72, 0.63, 0.56], isSensor: false },
      ];
      for (let i = 0; i < 8; i++) {
        const a = -Math.PI/2 + (i / 8) * Math.PI;
        const px = x + tr * Math.cos(a);
        const pz = z + hd/2 + tr * Math.sin(a);
        bodies.push({ func: 'axis_rotated_box', position: [px, y + 0.5, pz], halfExtents: [0.15, 0.5, 0.3], axis: [0, 1, 0], angle: -a, density: 0, friction: 0.5, restitution: 0.2, shapeType: RENDER_WALL, color: [0.67, 0.4, 0.27], isSensor: false });
      }
      return bodies;
    },
  },

  staggered_pegs: {
    type: 'staggered_pegs', label: 'Staggered Pegs', color: '#ff8844',
    defaultProperties: { width: 8, depth: 5, rows: 4, cols: 4 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      const r = seg.properties.rows; const c = seg.properties.cols;
      g.add(makeFloorBox(w, 0.5, d, '#d8c8b8'));
      for (let i = 0; i < r; i++) {
        const offset = (i % 2 === 0) ? 0 : w / (c * 2);
        for (let j = 0; j < c; j++) {
          const px = -w/2 + 1 + offset + j * (w - 2) / Math.max(c - 1, 1);
          const pz = -d/2 + 0.7 + i * (d - 1.4) / Math.max(r - 1, 1);
          const peg = makePegSphere(0.25, '#ff8844');
          peg.position.set(px, 0.25, pz);
          g.add(peg);
        }
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const r = seg.properties.rows; const c = seg.properties.cols;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.85, 0.78, 0.72], isSensor: false },
        { func: 'box', position: [x - sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      for (let i = 0; i < r; i++) {
        const offset = (i % 2 === 0) ? 0 : w / (c * 2);
        for (let j = 0; j < c; j++) {
          const px = x - hw + 1 + offset + j * (w - 2) / Math.max(c - 1, 1);
          const pz = z - hd + 0.7 + i * (d - 1.4) / Math.max(r - 1, 1);
          bodies.push({ func: 'sphere', position: [px, y, pz], radius: 0.25, density: 0, friction: 0.3, restitution: 0.3, shapeType: RENDER_PEG, color: [1.0, 0.53, 0.27], isSensor: false, velocity: [0,0,0] });
        }
      }
      return bodies;
    },
  },

  jump_gap: {
    type: 'jump_gap', label: 'Jump Gap', color: '#4488aa',
    defaultProperties: { width: 8, depth: 6, gapLength: 2 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth; const gl = seg.properties.gapLength;
      const rampLen = (d - gl) / 2; const angle = 0.3;
      const up = makeFloorBox(w, 0.5, rampLen, '#4488aa');
      up.position.set(0, 0, -d/2 + rampLen/2);
      up.rotation.x = -angle; g.add(up);
      // Walls for up ramp
      const upLeft = makeWallBox(0.2, 1.5, rampLen, '#4488dd');
      upLeft.position.set(-w/2 - 0.1, 0, -d/2 + rampLen/2);
      upLeft.rotation.x = -angle; g.add(upLeft);
      const upRight = makeWallBox(0.2, 1.5, rampLen, '#4488dd');
      upRight.position.set(w/2 + 0.1, 0, -d/2 + rampLen/2);
      upRight.rotation.x = -angle; g.add(upRight);
      // Down ramp
      const dn = makeFloorBox(w, 0.5, rampLen, '#4488aa');
      dn.position.set(0, 0, d/2 - rampLen/2);
      dn.rotation.x = angle; g.add(dn);
      // Walls for down ramp
      const dnLeft = makeWallBox(0.2, 1.5, rampLen, '#4488dd');
      dnLeft.position.set(-w/2 - 0.1, 0, d/2 - rampLen/2);
      dnLeft.rotation.x = angle; g.add(dnLeft);
      const dnRight = makeWallBox(0.2, 1.5, rampLen, '#4488dd');
      dnRight.position.set(w/2 + 0.1, 0, d/2 - rampLen/2);
      dnRight.rotation.x = angle; g.add(dnRight);
      // Catch net
      const catchNet = makeFloorBox(w, 0.1, gl * 0.8, '#6688aa');
      catchNet.position.set(0, -1.2, 0);
      g.add(catchNet);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth; const gl = seg.properties.gapLength;
      const rampLen = (d - gl) / 2; const hd = d / 2; const angle = 0.3; const hw = w / 2;
      return [
        { func: 'rotated_box', position: [x, y, z - hd + rampLen/2], halfExtents: [hw, 0.25, rampLen/2], rotationX: -angle, density: 0, friction: 0.35, restitution: 0.05, shapeType: RENDER_RAMP, color: [0.27, 0.53, 0.67], isSensor: false },
        { func: 'rotated_box', position: [x, y, z + hd - rampLen/2], halfExtents: [hw, 0.25, rampLen/2], rotationX: angle, density: 0, friction: 0.35, restitution: 0.05, shapeType: RENDER_RAMP, color: [0.27, 0.53, 0.67], isSensor: false },
        { func: 'box', position: [x, y - 1.2, z], halfExtents: [hw, 0.1, gl * 0.4], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.4, 0.53, 0.67], isSensor: false },
        { func: 'box', position: [x - hw - 0.15, y + 0.5, z - hd + rampLen/2], halfExtents: [0.1, 0.5, rampLen/2], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + hw + 0.15, y + 0.5, z - hd + rampLen/2], halfExtents: [0.1, 0.5, rampLen/2], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x - hw - 0.15, y + 0.5, z + hd - rampLen/2], halfExtents: [0.1, 0.5, rampLen/2], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + hw + 0.15, y + 0.5, z + hd - rampLen/2], halfExtents: [0.1, 0.5, rampLen/2], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
    },
  },

  teeth_walls: {
    type: 'teeth_walls', label: 'Teeth Walls', color: '#dd5544',
    defaultProperties: { width: 8, depth: 5, teethPerSide: 5, toothLength: 1.0 },
    createVisual: (seg) => {
      const g = new THREE.Group(); const w = seg.properties.width; const d = seg.properties.depth;
      const tps = seg.properties.teethPerSide; const tl = seg.properties.toothLength;
      g.add(makeFloorBox(w, 0.5, d, '#d0b8b0'));
      for (let i = 0; i < tps; i++) {
        const pz = -d/2 + (i + 0.5) * d / tps;
        const left = makeWallBox(tl, 0.4, 0.3, '#dd5544');
        left.position.set(-w/2 + tl/2, 0.2, pz);
        g.add(left);
        const right = makeWallBox(tl, 0.4, 0.3, '#dd5544');
        right.position.set(w/2 - tl/2, 0.2, pz + d / tps / 2);
        g.add(right);
      }
      addStandardSideWalls(g, w, d);
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position; const w = seg.properties.width; const d = seg.properties.depth;
      const tps = seg.properties.teethPerSide; const tl = seg.properties.toothLength;
      const hw = w / 2, hd = d / 2; const sx = hw + 0.15;
      const bodies: BodyConfig[] = [
        { func: 'box', position: [x, y, z], halfExtents: [hw, 0.25, hd], density: 0, friction: 0.4, restitution: 0, shapeType: RENDER_PLATFORM, color: [0.82, 0.72, 0.69], isSensor: false },
        { func: 'box', position: [x - sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
        { func: 'box', position: [x + sx, y + 0.75, z], halfExtents: [0.1, 0.75, hd], density: 0, friction: 0.5, restitution: 0, shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false },
      ];
      for (let i = 0; i < tps; i++) {
        const pz1 = z - hd + (i + 0.5) * d / tps;
        bodies.push({ func: 'box', position: [x - hw + tl/2, y + 0.2, pz1], halfExtents: [tl/2, 0.2, 0.15], density: 0, friction: 0.5, restitution: 0.3, shapeType: RENDER_WALL, color: [0.87, 0.33, 0.27], isSensor: false });
        const pz2 = z - hd + (i + 0.5) * d / tps + d / tps / 2;
        bodies.push({ func: 'box', position: [x + hw - tl/2, y + 0.2, pz2], halfExtents: [tl/2, 0.2, 0.15], density: 0, friction: 0.5, restitution: 0.3, shapeType: RENDER_WALL, color: [0.87, 0.33, 0.27], isSensor: false });
      }
      return bodies;
    },
  },

  corner_right: {
    type: 'corner_right', label: 'Right Corner', color: '#88bbdd',
    defaultProperties: { radius: 4, angle: 90, trackWidth: 6, segments: 10 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const r = seg.properties.radius;
      const tw = seg.properties.trackWidth;
      const arcDeg = seg.properties.angle;
      const count = Math.max(seg.properties.segments, 4);
      const arc = THREE.MathUtils.degToRad(arcDeg);
      const segLen = r * arc / count;
      const hw = tw / 2;
      for (let i = 0; i < count; i++) {
        const a = -arc/2 + (i + 0.5) * arc / count;
        // Floor segment
        const floor = makeFloorBox(tw, 0.4, segLen * 1.2, '#88bbdd');
        floor.position.set(r * Math.cos(a), 0, r * Math.sin(a));
        floor.rotation.y = -a;
        g.add(floor);
        // Outer wall
        const ow = makeWallBox(0.2, 1.2, segLen * 1.2, '#4488dd');
        ow.position.set((r + hw) * Math.cos(a), 0.6, (r + hw) * Math.sin(a));
        ow.rotation.y = -a;
        g.add(ow);
        // Inner wall
        const iw = makeWallBox(0.2, 1.2, segLen * 1.2, '#4488dd');
        iw.position.set((r - hw) * Math.cos(a), 0.6, (r - hw) * Math.sin(a));
        iw.rotation.y = -a;
        g.add(iw);
      }
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const r = seg.properties.radius;
      const tw = seg.properties.trackWidth;
      const arcDeg = seg.properties.angle;
      const count = Math.max(seg.properties.segments, 4);
      const arc = arcDeg * Math.PI / 180;
      const hw = tw / 2;
      const segLen = r * arc / count * 1.2;
      const halfW = segLen / 2;
      const bodies: BodyConfig[] = [];
      for (let i = 0; i < count; i++) {
        const a = -arc/2 + (i + 0.5) * arc / count;
        const ca = Math.cos(a), sa = Math.sin(a);
        // Floor
        bodies.push({
          func: 'axis_rotated_box',
          position: [x + r * ca, y, z + r * sa],
          halfExtents: [hw, 0.2, halfW],
          axis: [0, 1, 0], angle: -a,
          density: 0, friction: 0.4, restitution: 0,
          shapeType: RENDER_PLATFORM, color: [0.53, 0.73, 0.87], isSensor: false,
        });
        // Outer wall
        bodies.push({
          func: 'axis_rotated_box',
          position: [x + (r + hw) * ca, y + 0.6, z + (r + hw) * sa],
          halfExtents: [0.1, 0.6, halfW],
          axis: [0, 1, 0], angle: -a,
          density: 0, friction: 0.5, restitution: 0,
          shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false,
        });
        // Inner wall
        bodies.push({
          func: 'axis_rotated_box',
          position: [x + (r - hw) * ca, y + 0.6, z + (r - hw) * sa],
          halfExtents: [0.1, 0.6, halfW],
          axis: [0, 1, 0], angle: -a,
          density: 0, friction: 0.5, restitution: 0,
          shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false,
        });
      }
      return bodies;
    },
  },

  corner_left: {
    type: 'corner_left', label: 'Left Corner', color: '#88bbdd',
    defaultProperties: { radius: 4, angle: 90, trackWidth: 6, segments: 10 },
    createVisual: (seg) => {
      const g = new THREE.Group();
      const r = seg.properties.radius;
      const tw = seg.properties.trackWidth;
      const arcDeg = seg.properties.angle;
      const count = Math.max(seg.properties.segments, 4);
      const arc = THREE.MathUtils.degToRad(arcDeg);
      const segLen = r * arc / count;
      const hw = tw / 2;
      for (let i = 0; i < count; i++) {
        const a = -arc/2 + (i + 0.5) * arc / count;
        const ca = Math.cos(a), sa = Math.sin(a);
        const floor = makeFloorBox(tw, 0.4, segLen * 1.2, '#88bbdd');
        floor.position.set(-r * ca, 0, r * sa);
        floor.rotation.y = a;
        g.add(floor);
        const ow = makeWallBox(0.2, 1.2, segLen * 1.2, '#4488dd');
        ow.position.set(-(r + hw) * ca, 0.6, (r + hw) * sa);
        ow.rotation.y = a;
        g.add(ow);
        const iw = makeWallBox(0.2, 1.2, segLen * 1.2, '#4488dd');
        iw.position.set(-(r - hw) * ca, 0.6, (r - hw) * sa);
        iw.rotation.y = a;
        g.add(iw);
      }
      return g;
    },
    createBodyConfigs: (seg) => {
      const [x, y, z] = seg.position;
      const r = seg.properties.radius;
      const tw = seg.properties.trackWidth;
      const arcDeg = seg.properties.angle;
      const count = Math.max(seg.properties.segments, 4);
      const arc = arcDeg * Math.PI / 180;
      const hw = tw / 2;
      const segLen = r * arc / count * 1.2;
      const halfW = segLen / 2;
      const bodies: BodyConfig[] = [];
      for (let i = 0; i < count; i++) {
        const a = -arc/2 + (i + 0.5) * arc / count;
        const ca = Math.cos(a), sa = Math.sin(a);
        bodies.push({
          func: 'axis_rotated_box',
          position: [x - r * ca, y, z + r * sa],
          halfExtents: [hw, 0.2, halfW],
          axis: [0, 1, 0], angle: a,
          density: 0, friction: 0.4, restitution: 0,
          shapeType: RENDER_PLATFORM, color: [0.53, 0.73, 0.87], isSensor: false,
        });
        bodies.push({
          func: 'axis_rotated_box',
          position: [x - (r + hw) * ca, y + 0.6, z + (r + hw) * sa],
          halfExtents: [0.1, 0.6, halfW],
          axis: [0, 1, 0], angle: a,
          density: 0, friction: 0.5, restitution: 0,
          shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false,
        });
        bodies.push({
          func: 'axis_rotated_box',
          position: [x - (r - hw) * ca, y + 0.6, z + (r - hw) * sa],
          halfExtents: [0.1, 0.6, halfW],
          axis: [0, 1, 0], angle: a,
          density: 0, friction: 0.5, restitution: 0,
          shapeType: RENDER_WALL, color: [0.2, 0.45, 0.8], isSensor: false,
        });
      }
      return bodies;
    },
  },
};

export const SEGMENT_TYPES: SegmentType[] = [
  'start_platform',
  'straight_ramp',
  'flat_track',
  'peg_section',
  'splitter',
  'bumper',
  'finish_line',
  'wall',
  'curved_wall',
  'bump_section',
  'notch_walls',
  'slalom',
  'funnel',
  'steps_staircase',
  'wave_track',
  'tunnel',
  'banked_turn',
  'cascade',
  's_curve',
  'pinball_bumpers',
  'trampoline',
  'sawtooth',
  'grid_ramp',
  'choke_point',
  'hairpin',
  'staggered_pegs',
  'jump_gap',
  'teeth_walls',
  'corner_left',
  'corner_right',
];

// === SERIALIZATION ===

export function createDefaultLevel(name: string): LevelData {
  return { name, author: 'Ray', version: 1, segments: [] };
}

export function serializeLevel(level: LevelData): string {
  return JSON.stringify(level, null, 2);
}

export function deserializeLevel(json: string): LevelData {
  return JSON.parse(json) as LevelData;
}

export function downloadLevel(level: LevelData) {
  const json = serializeLevel(level);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${level.name.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function uploadLevel(): Promise<LevelData> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    const status = document.createElement('div');
    input.type = 'file';
    input.accept = '.json';
    input.style.display = 'none';
    status.id = 'load-status';
    status.textContent = 'Loading...';
    status.style.display = 'none';
    status.style.position = 'absolute';
    status.style.top = '16px';
    status.style.left = '50%';
    status.style.transform = 'translateX(-50%)';
    status.style.zIndex = '30';
    status.style.color = '#fff';
    status.style.background = 'rgba(0,0,0,0.75)';
    status.style.padding = '8px 12px';
    status.style.borderRadius = '6px';
    document.body.appendChild(input);
    document.body.appendChild(status);
    const cleanup = () => {
      if (input.parentNode) document.body.removeChild(input);
      if (status.parentNode) document.body.removeChild(status);
    };
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) { cleanup(); reject(new Error('No file selected')); return; }
      status.style.display = '';
      const reader = new FileReader();
      reader.onload = () => {
        try {
          resolve(deserializeLevel(reader.result as string));
        } catch (e) {
          reject(new Error('Invalid level JSON'));
        } finally {
          cleanup();
        }
      };
      reader.onerror = () => {
        cleanup();
        reject(new Error('Failed to read file'));
      };
      reader.readAsText(file);
    };
    input.click();
  });
}
